import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, Input, NativeSelect } from "@ncp/ui";
import { DECISION_OPS, type DecisionCell, type DecisionOp, type DecisionTable, type RuleField, type RuleKind } from "./api";

interface Props { table: DecisionTable; onChange: (t: DecisionTable) => void; fields: RuleField[]; kind: RuleKind; readOnly?: boolean }

const NO_VALUE = new Set<DecisionOp>(["any", "empty", "filled"]);

function typed(raw: string, type?: string | null): unknown {
  if (type === "number") { const n = Number(raw); return raw.trim() !== "" && Number.isFinite(n) ? n : raw; }
  if (type === "boolean") return raw === "true";
  return raw;
}

const show = (v: unknown) => (v === undefined || v === null ? "" : Array.isArray(v) ? v.join(", ") : String(v));

/** A grid of rules: one column per input, one row per case, read top to bottom – the first row whose cells all match gives the outcome. */
export function DecisionTableEditor({ table, onChange, fields, kind, readOnly }: Props) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const yesNo = kind !== "Computation";
  const label = (f: RuleField) => (locale === "ar" && f.labelAr ? f.labelAr : f.label);
  const setCell = (r: number, key: string, cell: DecisionCell) => onChange({ ...table, rows: table.rows.map((row, i) => (i === r ? { ...row, cells: { ...row.cells, [key]: cell } } : row)) });
  const setOutput = (r: number, output: unknown) => onChange({ ...table, rows: table.rows.map((row, i) => (i === r ? { ...row, output } : row)) });
  const move = (r: number, d: -1 | 1) => { const rows = [...table.rows]; const [x] = rows.splice(r, 1); rows.splice(r + d, 0, x!); onChange({ ...table, rows }); };
  const nextKey = () => { let n = table.inputs.length + 1; while (table.inputs.some((i) => i.key === `in${n}`)) n++; return `in${n}`; };

  const outputEditor = (value: unknown, set: (v: unknown) => void, testId: string) => yesNo ? (
    <NativeSelect data-testid={testId} value={value === true || value === "true" ? "true" : "false"} disabled={readOnly} onChange={(e) => set(e.target.value === "true")} className="h-8">
      <option value="true">{t("admin:rules.table.yes")}</option>
      <option value="false">{t("admin:rules.table.no")}</option>
    </NativeSelect>
  ) : <Input data-testid={testId} className="h-8" value={show(value)} disabled={readOnly} onChange={(e) => set(typed(e.target.value, Number.isFinite(Number(e.target.value)) ? "number" : "text"))} />;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-sand-200">
        <table className="w-full text-sm">
          <thead className="bg-sand-50 text-xs text-muted-foreground">
            <tr>
              <th className="w-10 px-2 py-2 text-start font-medium">#</th>
              {table.inputs.map((input, c) => (
                <th key={input.key} className="min-w-56 px-2 py-2 text-start font-medium">
                  <div className="flex items-center gap-1">
                    <NativeSelect data-testid={`table-input-${c}`} aria-label={t("admin:rules.table.input")} value={input.path} disabled={readOnly} className="h-8"
                      onChange={(e) => { const f = fields.find((x) => x.path === e.target.value); onChange({ ...table, inputs: table.inputs.map((x, i) => (i === c ? { ...x, path: e.target.value, label: f?.label ?? e.target.value, labelAr: f?.labelAr ?? null, type: f?.type ?? "text" } : x)) }); }}>
                      {!fields.some((f) => f.path === input.path) ? <option value={input.path}>{input.label ?? input.path}</option> : null}
                      {fields.map((f) => <option key={f.path} value={f.path}>{label(f)}</option>)}
                    </NativeSelect>
                    {!readOnly && table.inputs.length > 1 ? <Button size="icon-sm" variant="ghost" aria-label={t("admin:common.remove")} onClick={() => onChange({ inputs: table.inputs.filter((_, i) => i !== c), rows: table.rows.map((r) => { const cells = { ...r.cells }; delete cells[input.key]; return { ...r, cells }; }), default: table.default })}><Trash2 /></Button> : null}
                  </div>
                </th>
              ))}
              <th className="min-w-36 bg-aegold-50/60 px-2 py-2 text-start font-medium">{yesNo ? t("admin:rules.table.holds") : t("admin:rules.table.result")}</th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody className="divide-y divide-sand-100">
            {table.rows.map((row, r) => (
              <tr key={r}>
                <td className="px-2 py-1.5 text-xs text-muted-foreground tabular-nums">{r + 1}</td>
                {table.inputs.map((input) => {
                  const cell = row.cells[input.key] ?? { op: "any" as DecisionOp };
                  const field = fields.find((f) => f.path === input.path);
                  const type = input.type ?? field?.type;
                  return (
                    <td key={input.key} className="px-2 py-1.5">
                      <div className="flex gap-1">
                        <NativeSelect data-testid={`cell-op-${r}-${input.key}`} aria-label={t("admin:rules.table.operator")} value={cell.op} disabled={readOnly} className="h-8 w-28 shrink-0"
                          onChange={(e) => { const op = e.target.value as DecisionOp; setCell(r, input.key, { op, value: NO_VALUE.has(op) ? undefined : op === "between" ? [0, 0] : op === "oneOf" ? [] : cell.value }); }}>
                          {DECISION_OPS.map((op) => <option key={op} value={op}>{t(`admin:rules.ops.${op}`)}</option>)}
                        </NativeSelect>
                        {NO_VALUE.has(cell.op) ? null : cell.op === "between" ? (
                          <div className="flex gap-1">
                            {[0, 1].map((i) => <Input key={i} className="h-8 w-20" value={show((cell.value as unknown[] | undefined)?.[i])} disabled={readOnly} onChange={(e) => { const range = [...((cell.value as unknown[]) ?? [0, 0])]; range[i] = typed(e.target.value, type); setCell(r, input.key, { ...cell, value: range }); }} />)}
                          </div>
                        ) : field?.options?.length && cell.op === "eq" ? (
                          <NativeSelect className="h-8" value={show(cell.value)} disabled={readOnly} onChange={(e) => setCell(r, input.key, { ...cell, value: e.target.value })}>
                            <option value="">—</option>
                            {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </NativeSelect>
                        ) : (
                          <Input data-testid={`cell-value-${r}-${input.key}`} className="h-8" value={show(cell.value)} disabled={readOnly} placeholder={cell.op === "oneOf" ? t("admin:rules.table.commaList") : ""}
                            onChange={(e) => setCell(r, input.key, { ...cell, value: cell.op === "oneOf" ? e.target.value.split(",").map((x) => typed(x.trim(), type)).filter((x) => x !== "") : typed(e.target.value, type) })} />
                        )}
                      </div>
                    </td>
                  );
                })}
                <td className="bg-aegold-50/30 px-2 py-1.5">{outputEditor(row.output, (v) => setOutput(r, v), `row-output-${r}`)}</td>
                <td className="px-1 py-1.5">
                  {!readOnly ? (
                    <div className="flex">
                      <Button size="icon-sm" variant="ghost" disabled={r === 0} aria-label={t("admin:rules.table.up")} onClick={() => move(r, -1)}><ArrowUp /></Button>
                      <Button size="icon-sm" variant="ghost" disabled={r === table.rows.length - 1} aria-label={t("admin:rules.table.down")} onClick={() => move(r, 1)}><ArrowDown /></Button>
                      <Button size="icon-sm" variant="ghost" aria-label={t("admin:common.remove")} onClick={() => onChange({ ...table, rows: table.rows.filter((_, i) => i !== r) })}><Trash2 /></Button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
            <tr className="bg-sand-50/60">
              <td />
              <td colSpan={table.inputs.length} className="px-2 py-1.5 text-xs text-muted-foreground">{t("admin:rules.table.otherwise")}</td>
              <td className="bg-aegold-50/30 px-2 py-1.5">{outputEditor(table.default, (v) => onChange({ ...table, default: v }), "row-default")}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      {!readOnly ? (
        <div className="flex flex-wrap gap-2">
          <Button data-testid="table-add-row" size="sm" variant="outline" onClick={() => onChange({ ...table, rows: [...table.rows, { cells: {}, output: yesNo ? true : "" }] })}><Plus /> {t("admin:rules.table.addRow")}</Button>
          <Button data-testid="table-add-input" size="sm" variant="outline" onClick={() => onChange({ ...table, inputs: [...table.inputs, { key: nextKey(), path: fields[0]?.path ?? "status", label: fields[0]?.label ?? "status", type: fields[0]?.type ?? "text" }] })}><Plus /> {t("admin:rules.table.addInput")}</Button>
        </div>
      ) : null}
    </div>
  );
}
